import { useState } from 'react'

import {
  Avatar,
  BookmarkOne,
  CheckOne,
  DocumentFolder,
  Down,
  Earth,
  FolderOpen,
  Help,
  People,
  Save,
  SmilingFace,
  SpeakerOne,
  Star,
  Tag,
  Target,
  Time,
  TrendingUp,
  Up,
  Editor,
  MaslowPyramids,
} from '@icon-park/react'

import { Tokens } from '@/assets/tokens'

import { Button, Flex, Icon, Input, Selector, Tooltip } from '@/components'

import { useRouting } from '@/hooks'

import { SearchResponse } from '@shared/models'

import { REVIEWED_OPTIONS, useMentionsContext } from '../../Mentions.context'

import {
  AccordionIcon,
  ActionButton,
  ClearFiltersButton,
  FilterActionsContainer,
  FilterGroup,
  FilterGroupLine,
  FilterHeaderContainer,
  InvisibleArea,
  MainFiltersContainer,
  MentionsFilterContainer,
  MentionsFilterHeader,
  MentionsFilterItemContainer,
  TimeInputContainer,
  ToggleAccordionButton,
  ToggleText,
} from './Filters.styles'

import { DatePickerWrapper } from '@/components/core/DatePicker/DatePickerWrapper.component'
import { BooleanTooltipContent } from '@/components/core/Tooltip/BooleanTooltip'
import { LoadFilterModal, SaveFilterModal } from '@/components/shared/FilterModal'
import { Alert, Api } from '@/modules'
import { Str } from '@/utils/strings'

function groupThemesByName(themes: SearchResponse[]): SearchResponse[] {
  const groups = new Map<string, string[]>()
  for (const t of themes) {
    const key = t.name ?? t.text
    groups.set(key, [...(groups.get(key) ?? []), String(t.value)])
  }
  return Array.from(groups, ([name, values]) => ({ text: name, value: values.join(',') }))
}

export function Filters() {
  const mentionsContext = useMentionsContext()
  if (!mentionsContext.startDateBinder || !mentionsContext.endDateBinder) {
    return null
  }

  const {
    applyFilters,
    resetFilters,
    downloadSpreadsheet,

    startDateBinder,
    endDateBinder,
    startTimeBinder,
    endTimeBinder,

    approachBinder,
    brandBinder,
    channelBinder,
    contentBinder,
    hasChangesBinder,
    reviewedBinder,
    engagementTypeBinder,
    engagementMaxBinder,
    engagementMinBinder,
    polarityBinder,
    profileBinder,
    publisherBinder,
    roleBinder,
    keyMessageBinder,
    tierBinder,
    sourceBinder,
    spokespersonBinder,
    themeBinder,
    themeGroupBinder,

    hasContentIssuesBinder,

    isSearchingBrands,
    isSearchingChannels,
    isSearchingRoles,
    isSearchingKeyMessages,
    isSearchingTiers,
    isSearchingApproaches,
    isSearchingThemeGroups,
    isSearchingThemes,
    isSearchingPublishers,
    isSearchingSpokespersons,
    isSearchingProfiles,
    isLoadingMentions,
    isExporting,

    brands,
    polarities,
    channels,
    roles,
    keyMessages,
    tiers,
    approaches,
    themeGroups,
    themes,
    publishers,
    spokespersons,
    profiles,
    handleProfileSearch,
  } = mentionsContext

  const toggleAccordion = () => {
    setIsOpen(!isOpen)
  }

  const [isOpen, setIsOpen] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [showLoadModal, setShowLoadModal] = useState(false)
  const { sendAlert } = Alert.Hook()
  const { companyID } = useRouting()

  const [searchThemesForFilterConfig, isRestoringThemeFilter] = Api.PromiseQuery<SearchResponse[]>('themes/search')

  const handleApplyFilters = () => {
    const startDate = startDateBinder.stringValue
    const endDate = endDateBinder.stringValue
    const hasBrands = brandBinder.selectedOptions.length > 0

    const engagementMin = engagementMinBinder.value
    const engagementMax = engagementMaxBinder.value
    const engagementType = engagementTypeBinder.selectedOption?.value

    const validations = [
      [!startDate || !endDate, 'A data inicial e a data final são obrigatórias.'],
      [!hasBrands, 'Selecione pelo menos uma marca.'],
      [
        engagementMin && (isNaN(Number(engagementMin)) || Number(engagementMin) < 0),
        'O valor mínimo de engajamento deve ser um número maior ou igual a zero.',
      ],
      [
        engagementMax && (isNaN(Number(engagementMax)) || Number(engagementMax) < 0),
        'O valor máximo de engajamento deve ser um número maior ou igual a zero.',
      ],
      [
        engagementMin && engagementMax && Number(engagementMin) > Number(engagementMax),
        'O valor mínimo de engajamento deve ser menor que o máximo.',
      ],
      [
        (engagementMin || engagementMax) && !engagementType,
        'Selecione o tipo de engajamento quando definir valores mínimo ou máximo.',
      ],
    ] as const

    const error = validations.find(([condition]) => condition)

    if (error) {
      sendAlert({
        type: 'error',
        title: 'Não foi possível filtrar',
        message: error[1],
      })
      return
    }

    applyFilters()
  }

  const handleExport = async () => {
    try {
      const { message, status } = await downloadSpreadsheet()
      if (status !== 202) {
        sendAlert({
          type: 'error',
          title: 'Erro',
          message: message,
        })
        return
      }

      sendAlert({
        type: 'success',
        title: 'Sucesso',
        message: message,
      })
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao exportar dados.',
      })
    }
  }

  const engagementOptions = [
    { text: 'Comentários', value: 'comments' },
    { text: 'Compartilhamentos', value: 'shares' },
    { text: 'Curtidas', value: 'likes' },
    { text: 'Reações', value: 'reactions' },
    { text: 'Seguidores', value: 'followers' },
    { text: 'Visualizações', value: 'views' },
    { text: 'Total de Interações', value: 'totalInteractions' },
  ]

  const hasChangesOptions = [
    {
      text: 'Com edição',
      value: 'COM',
    },
    {
      text: 'Sem edição',
      value: 'SEM',
    },
  ]

  const hasContentIssuesOptions = [
    {
      text: 'Título igual ao conteúdo',
      value: 'COM',
    },
    {
      text: 'Título diferente do conteúdo',
      value: 'SEM',
    },
  ]

  const getCurrentFilterConfig = () => {
    const polarityValues = polarityBinder.selectedOptions.map((p) => p.value)
    const excludedPolarityValues = polarityBinder.excludedOptions?.map((p) => p.value) || []

    const channelValues = channelBinder.selectedOptions.map((c) => Number(c.value))
    const excludedChannelValues = channelBinder.excludedOptions?.map((c) => Number(c.value)) || []

    const roleValues = roleBinder.selectedOptions.map((r) => Number(r.value))
    const excludedRoleValues = roleBinder.excludedOptions?.map((r) => Number(r.value)) || []

    const keyMessageValues = keyMessageBinder.selectedOptions.map((k) => k.value)
    const excludedKeyMessageValues = keyMessageBinder.excludedOptions?.map((k) => k.value) || []

    const tierValues = tierBinder.selectedOptions.map((p) => p.text)
    const excludedTierValues = tierBinder.excludedOptions?.map((p) => p.text) || []

    const approachValues = approachBinder.selectedOptions.map((a) => Number(a.value))
    const excludedApproachValues = approachBinder.excludedOptions?.map((a) => Number(a.value)) || []

    const themeGroupValues = themeGroupBinder.selectedOptions.map((tg) => String(tg.value))
    const excludedThemeGroupValues = themeGroupBinder.excludedOptions?.map((tg) => String(tg.value)) || []

    const themeValues = themeBinder.selectedOptions.map((t) => String(t.value))
    const excludedThemeValues = themeBinder.excludedOptions?.map((t) => String(t.value)) || []

    const publisherValues = publisherBinder.selectedOptions.map((p) => Number(p.value))
    const excludedPublisherValues = publisherBinder.excludedOptions?.map((p) => Number(p.value)) || []

    const sourceValues = sourceBinder.selectedOptions.map((s) => Number(s.value))
    const excludedSourceValues = sourceBinder.excludedOptions?.map((s) => Number(s.value)) || []

    const spokespersonValues = spokespersonBinder.selectedOptions.map((s) => String(s.value))
    const excludedSpokespersonValues = spokespersonBinder.excludedOptions?.map((s) => String(s.value)) || []

    const profileValues = profileBinder.selectedOptions.map((p) => p.value)
    const excludedProfileValues = profileBinder.excludedOptions?.map((p) => p.value) || []

    return {
      datefrom: startDateBinder.stringValue,
      dateto: endDateBinder.stringValue,
      brand: brandBinder.selectedOptions.map((b) => b.value),
      polarity: [...polarityValues, ...excludedPolarityValues.map((p) => `!${p}`)].filter(Boolean),
      channel: [...channelValues, ...excludedChannelValues.map((c) => -Math.abs(c))].filter((v) => v !== 0),
      role: [...roleValues, ...excludedRoleValues.map((r) => -Math.abs(r))].filter((v) => v !== 0),
      keyMessage: [...keyMessageValues, ...excludedKeyMessageValues.map((k) => `!${k}`)].filter(Boolean),
      tier: [...tierValues, ...excludedTierValues.map((p) => `!${p}`)].filter(Boolean),
      approach: [...approachValues, ...excludedApproachValues.map((a) => -Math.abs(a))].filter((v) => v !== 0),
      grouptheme: [...themeGroupValues, ...excludedThemeGroupValues.map((tg) => `!${tg}`)].filter(Boolean),
      theme: [...themeValues, ...excludedThemeValues.map((t) => `!${t}`)].filter(Boolean),
      publisher: [...publisherValues, ...excludedPublisherValues.map((p) => -Math.abs(p))].filter((v) => v !== 0),
      source: [...sourceValues, ...excludedSourceValues.map((s) => -Math.abs(s))].filter((v) => v !== 0),
      hourfrom: startTimeBinder.value || '',
      hourto: endTimeBinder.value || '',
      content: contentBinder.value || '',
      engagementType: engagementTypeBinder.selectedOption?.value || '',
      engagementMin: engagementMinBinder.value ? Number(engagementMinBinder.value) : undefined,
      engagementMax: engagementMaxBinder.value ? Number(engagementMaxBinder.value) : undefined,
      spokesperson: [...spokespersonValues, ...excludedSpokespersonValues.map((s) => `!${s}`)].filter(Boolean),
      profiles: [...profileValues, ...excludedProfileValues.map((p) => `!${p}`)].filter(Boolean),
      hasChanges: hasChangesBinder.selectedOption?.value || '',
      hasContentIssues: hasContentIssuesBinder.selectedOption?.value || '',
      reviewed: reviewedBinder.selectedOption?.value || '',
    }
  }

  const applyFilterConfig = async (config: any) => {
    resetFilters()

    if (config.datefrom) {
      const [day, month, year] = config.datefrom.split('/')
      const startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      startDateBinder.onChange(startDate)
    }
    if (config.dateto) {
      const [day, month, year] = config.dateto.split('/')
      const endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      endDateBinder.onChange(endDate)
    }

    if (config.brand?.length) {
      const brandOptions = brands?.filter((b) => config.brand.includes(b.value)) || []
      brandBinder.clearAll()
      brandOptions.forEach((option) => brandBinder.onToggle(option, 'include'))
    }

    if (config.polarity?.length) {
      const includedPolarities = config.polarity.filter((p: string) => !p.startsWith('!'))
      const excludedPolarities = config.polarity
        .filter((p: string) => p.startsWith('!'))
        .map((p: string) => p.substring(1))

      const selectedPolarities = polarities?.filter((p) => includedPolarities.includes(p.value)) || []
      const excludedPolarityOptions = polarities?.filter((p) => excludedPolarities.includes(p.value)) || []

      polarityBinder.clearAll()
      selectedPolarities.forEach((option) => polarityBinder.onToggle(option, 'include'))
      excludedPolarityOptions.forEach((option) => polarityBinder.onToggle(option, 'exclude'))
    }

    if (config.channel?.length) {
      const includedChannels = config.channel.filter((c: number) => c > 0)
      const excludedChannels = config.channel.filter((c: number) => c < 0).map((c: number) => Math.abs(c))

      const selectedChannels = channels?.filter((c) => includedChannels.includes(Number(c.value))) || []
      const excludedChannelOptions = channels?.filter((c) => excludedChannels.includes(Number(c.value))) || []

      channelBinder.clearAll()
      selectedChannels.forEach((option) => channelBinder.onToggle(option, 'include'))
      excludedChannelOptions.forEach((option) => channelBinder.onToggle(option, 'exclude'))
    }

    if (config.role?.length) {
      const includedRoles = config.role.filter((r: number) => r > 0)
      const excludedRoles = config.role.filter((r: number) => r < 0).map((r: number) => Math.abs(r))

      const selectedRoles = roles?.filter((r) => includedRoles.includes(Number(r.value))) || []
      const excludedRoleOptions = roles?.filter((r) => excludedRoles.includes(Number(r.value))) || []

      roleBinder.clearAll()
      selectedRoles.forEach((option) => roleBinder.onToggle(option, 'include'))
      excludedRoleOptions.forEach((option) => roleBinder.onToggle(option, 'exclude'))
    }

    if (config.keyMessage?.length) {
      const includedKeyMessage = config.keyMessage.filter((m: string) => !m.startsWith('!'))
      const excludedKeyMessage = config.keyMessage
        .filter((p: string) => p.startsWith('!'))
        .map((p: string) => p.substring(1))

      const selectedKeyMessage =
        keyMessages
          ?.filter((m) => includedKeyMessage.includes(m.internalID))
          .map((m) => ({ text: m.message, value: m.internalID })) || []

      const excludedKeyMessageOptions =
        keyMessages
          ?.filter((m) => excludedKeyMessage.includes(m.internalID))
          .map((m) => ({ text: m.message, value: m.internalID })) || []

      keyMessageBinder.clearAll()
      selectedKeyMessage.forEach((option) => keyMessageBinder.onToggle(option, 'include'))
      excludedKeyMessageOptions.forEach((option) => keyMessageBinder.onToggle(option, 'exclude'))
    }

    if (config.tier?.length) {
      const includedTiers = config.tier.filter((p: string) => !p.startsWith('!'))
      const excludedTiers = config.tier.filter((p: string) => p.startsWith('!')).map((p: string) => p.substring(1))

      const selectedTiers = tiers?.filter((p) => includedTiers.includes(p.text)) || []
      const excludedTierOptions = tiers?.filter((p) => excludedTiers.includes(p.text)) || []

      tierBinder.clearAll()
      selectedTiers.forEach((option) => tierBinder.onToggle(option, 'include'))
      excludedTierOptions.forEach((option) => tierBinder.onToggle(option, 'exclude'))
    }

    if (config.approach?.length) {
      const includedApproaches = config.approach.filter((a: number) => a > 0)
      const excludedApproaches = config.approach.filter((a: number) => a < 0).map((a: number) => Math.abs(a))

      const selectedApproaches = approaches?.filter((a) => includedApproaches.includes(Number(a.value))) || []
      const excludedApproachOptions = approaches?.filter((a) => excludedApproaches.includes(Number(a.value))) || []

      approachBinder.clearAll()
      selectedApproaches.forEach((option) => approachBinder.onToggle(option, 'include'))
      excludedApproachOptions.forEach((option) => approachBinder.onToggle(option, 'exclude'))
    }

    let includedThemeGroupValues: string[] = []

    if (config.grouptheme?.length) {
      const themeGroupValues = config.grouptheme.filter(Boolean)
      const includedThemeGroups = themeGroupValues.filter((tg: string) => !tg.startsWith('!'))
      const excludedThemeGroups = themeGroupValues
        .filter((tg: string) => tg.startsWith('!'))
        .map((tg: string) => tg.substring(1))

      includedThemeGroupValues = includedThemeGroups

      const selectedThemeGroups = themeGroups?.filter((tg) => includedThemeGroups.includes(String(tg.value))) || []
      const excludedThemeGroupOptions =
        themeGroups?.filter((tg) => excludedThemeGroups.includes(String(tg.value))) || []

      themeGroupBinder.clearAll()
      selectedThemeGroups.forEach((option) => themeGroupBinder.onToggle(option, 'include'))
      excludedThemeGroupOptions.forEach((option) => themeGroupBinder.onToggle(option, 'exclude'))
    }

    if (config.theme?.length && includedThemeGroupValues.length) {
      const themeValues = config.theme.filter(Boolean)
      const includedThemes = themeValues.filter((t: string) => !t.startsWith('!'))
      const excludedThemes = themeValues.filter((t: string) => t.startsWith('!')).map((t: string) => t.substring(1))

      const activeBrands = brands?.filter((b) => config.brand?.includes(b.value)) || []
      const hasCompetitorBrands = activeBrands.some((brand) => brand.text.endsWith(' (C)'))

      if (companyID && activeBrands.length) {
        const [freshThemes] = await searchThemesForFilterConfig(undefined, {
          company: companyID,
          marca: activeBrands.map((b) => b.value).join(','),
          grupoTematico: includedThemeGroupValues.join(','),
          fathersOnly: !hasCompetitorBrands,
          orderBy: 'value:ASC',
          isActive: 'true',
        })

        const groupedFreshThemes = groupThemesByName(freshThemes ?? [])
        const selectedThemes = groupedFreshThemes.filter((g) =>
          g.value
            .toString()
            .split(',')
            .some((id) => includedThemes.includes(id)),
        )
        const excludedThemeOptions = groupedFreshThemes.filter((g) =>
          g.value
            .toString()
            .split(',')
            .some((id) => excludedThemes.includes(id)),
        )

        themeBinder.clearAll()
        selectedThemes.forEach((option) => themeBinder.onToggle(option, 'include'))
        excludedThemeOptions.forEach((option) => themeBinder.onToggle(option, 'exclude'))
      }
    }

    if (config.publisher?.length) {
      const includedPublishers = config.publisher.filter((p: number) => p > 0)
      const excludedPublishers = config.publisher.filter((p: number) => p < 0).map((p: number) => Math.abs(p))

      const selectedPublishers = publishers?.filter((p) => includedPublishers.includes(Number(p.value))) || []
      const excludedPublisherOptions = publishers?.filter((p) => excludedPublishers.includes(Number(p.value))) || []

      publisherBinder.clearAll()
      selectedPublishers.forEach((option) => publisherBinder.onToggle(option, 'include'))
      excludedPublisherOptions.forEach((option) => publisherBinder.onToggle(option, 'exclude'))
    }

    if (config.source?.length) {
      const includedSources = config.source.filter((s: number) => s > 0)
      const excludedSources = config.source.filter((s: number) => s < 0).map((s: number) => Math.abs(s))

      const sourceOptions = [
        { text: 'Redes', value: '1' },
        { text: 'Imprensa', value: '2' },
      ]

      const selectedSources = sourceOptions.filter((s) => includedSources.includes(Number(s.value)))
      const excludedSourceOptions = sourceOptions.filter((s) => excludedSources.includes(Number(s.value)))

      sourceBinder.clearAll()
      selectedSources.forEach((option) => sourceBinder.onToggle(option, 'include'))
      excludedSourceOptions.forEach((option) => sourceBinder.onToggle(option, 'exclude'))
    }

    if (config.spokesperson?.length) {
      const spokespersonValues = config.spokesperson.filter(Boolean)
      const includedSpokespersons = spokespersonValues.filter((s: string) => !s.startsWith('!'))
      const excludedSpokespersons = spokespersonValues
        .filter((s: string) => s.startsWith('!'))
        .map((s: string) => s.substring(1))

      const selectedSpokespersons = spokespersons?.filter((s) => includedSpokespersons.includes(s.value)) || []
      const excludedSpokespersonOptions = spokespersons?.filter((s) => excludedSpokespersons.includes(s.value)) || []

      spokespersonBinder.clearAll()
      selectedSpokespersons.forEach((option) => spokespersonBinder.onToggle(option, 'include'))
      excludedSpokespersonOptions.forEach((option) => spokespersonBinder.onToggle(option, 'exclude'))
    }

    if (config.profiles?.length) {
      const includedProfiles = config.profiles.filter((p: string) => !p.startsWith('!'))
      const excludedProfiles = config.profiles
        .filter((p: string) => p.startsWith('!'))
        .map((p: string) => p.substring(1))

      const selectedProfiles = profiles?.filter((p) => includedProfiles.includes(p.value)) || []
      const excludedProfileOptions = profiles?.filter((p) => excludedProfiles.includes(p.value)) || []

      profileBinder.clearAll()
      selectedProfiles.forEach((option) => profileBinder.onToggle(option, 'include'))
      excludedProfileOptions.forEach((option) => profileBinder.onToggle(option, 'exclude'))
    }

    if (config.hourfrom) startTimeBinder.onChange(config.hourfrom)
    if (config.hourto) endTimeBinder.onChange(config.hourto)
    if (config.content) contentBinder.onChange(config.content)
    if (config.engagementType) {
      const engagementOption = engagementOptions.find((e) => e.value === config.engagementType)
      engagementTypeBinder.onSelect(engagementOption)
    }
    if (config.engagementMin) engagementMinBinder.onChange(config.engagementMin.toString())
    if (config.engagementMax) engagementMaxBinder.onChange(config.engagementMax.toString())
    if (config.hasChanges) {
      const hasChangesOption = hasChangesOptions.find((e) => e.value === config.hasChanges)
      hasChangesBinder.onSelect(hasChangesOption)
    }
    if (config.hasContentIssues) {
      const hasContentIssuesOption = hasContentIssuesOptions.find((e) => e.value === config.hasContentIssues)
      hasContentIssuesBinder.onSelect(hasContentIssuesOption)
    }
    if (config.reviewed) {
      reviewedBinder.onSelect(REVIEWED_OPTIONS.find((option) => option.value === config.reviewed))
    }

    sendAlert({
      type: 'success',
      title: 'Filtro carregado',
      message: 'A configuração de filtro foi aplicada com sucesso.',
    })
  }

  const tooltipContent = <BooleanTooltipContent />

  const socialChannels = channels?.filter((ch) => ch.source === 1)
  const pressChannels = channels?.filter((ch) => ch.source === 2)

  const makeScopedBinder = (
    binder: typeof channelBinder,
    scopedOptions: typeof socialChannels,
  ): typeof channelBinder => {
    const scopedValues = new Set(scopedOptions?.map((o) => String(o.value)) ?? [])

    return {
      ...binder,
      selectedOptions: binder.selectedOptions.filter((o) => scopedValues.has(String(o.value))),
      excludedOptions: binder.excludedOptions?.filter((o) => scopedValues.has(String(o.value))) ?? [],
      onToggle: (option, mode) => {
        if (scopedValues.has(String(option.value))) {
          binder.onToggle(option, mode)
        }
      },
      clearAll: () => {
        binder.selectedOptions
          .filter((o) => scopedValues.has(String(o.value)))
          .forEach((o) => binder.onToggle(o, 'include'))
        binder.excludedOptions
          ?.filter((o) => scopedValues.has(String(o.value)))
          .forEach((o) => binder.onToggle(o, 'exclude'))
      },
    }
  }

  const socialChannelBinder = makeScopedBinder(channelBinder, socialChannels)
  const pressChannelBinder = makeScopedBinder(channelBinder, pressChannels)

  return (
    <FilterGroup>
      <MentionsFilterContainer>
        <MentionsFilterItemContainer>
          <MentionsFilterHeader>
            <FilterHeaderContainer>
              Filtros
              <FilterActionsContainer>
                <ActionButton onClick={() => setShowSaveModal(true)} title="Salvar configuração de filtro">
                  <Icon value={Save} color={Tokens.ColorUIPrimary} />
                  <span>Salvar</span>
                </ActionButton>
                <ActionButton onClick={() => setShowLoadModal(true)} title="Carregar configuração de filtro">
                  <Icon value={FolderOpen} color={Tokens.ColorUIPrimary} />
                  <span>Carregar</span>
                </ActionButton>
                <ClearFiltersButton onClick={resetFilters} disabled={isLoadingMentions}>
                  Limpar Filtros
                </ClearFiltersButton>
                <ToggleAccordionButton onClick={toggleAccordion}>
                  <ToggleText>{isOpen ? 'Menos' : 'Mais'}</ToggleText>
                  <AccordionIcon style={{ fill: Tokens.ColorUIPrimary, marginTop: '5px' }}>
                    {isOpen ?
                      <Icon value={Up} color={Tokens.ColorUIPrimary}></Icon>
                    : <Icon value={Down} color={Tokens.ColorUIPrimary}></Icon>}
                  </AccordionIcon>
                </ToggleAccordionButton>
              </FilterActionsContainer>
            </FilterHeaderContainer>

            <MainFiltersContainer className="main-filters">
              <DatePickerWrapper
                selected={startDateBinder.value}
                onChange={startDateBinder.onChange}
                placeholder="Início"
              />
              <TimeInputContainer>
                <Input icon={Time} placeholder="Início" {...startTimeBinder} />
              </TimeInputContainer>
              <DatePickerWrapper selected={endDateBinder.value} onChange={endDateBinder.onChange} placeholder="Fim" />
              <TimeInputContainer>
                <Input icon={Time} placeholder="Fim" {...endTimeBinder} />
              </TimeInputContainer>
              <Selector.Multi
                binder={brandBinder}
                placeholder="Marca"
                title="Marca"
                options={
                  brands?.map((item) => ({
                    text: item.text,
                    value: item.value,
                  })) ?? []
                }
                loading={isSearchingBrands}
                $fit={true}>
                <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
              </Selector.Multi>
              {isOpen ?
                <Button.Root
                  $variant="primaryOrange"
                  onClick={handleExport}
                  style={{ width: '180px', flexShrink: 0 }}
                  $loading={isExporting}>
                  <Button.Text value="Exportar" />
                </Button.Root>
              : <Button.Root
                  $variant="primary"
                  onClick={handleApplyFilters}
                  style={{ width: '180px', flexShrink: 0 }}
                  $loading={isLoadingMentions}>
                  <Button.Text value="Filtrar" />
                </Button.Root>
              }
            </MainFiltersContainer>
            <InvisibleArea $isOpen={isOpen}>
              <FilterGroupLine className="secondary-filters">
                <Selector.Multi
                  allowExclusion={true}
                  binder={polarityBinder}
                  placeholder="Polaridade"
                  title="Polaridade"
                  options={
                    polarities
                      ?.map((item) => ({
                        text: Str.from(item.text).initCap,
                        value: item.value,
                      }))
                      .filter((item) => item.text.toLowerCase() !== 'neutro') ?? []
                  }
                  $fit={true}>
                  <Selector.Icon value={SmilingFace} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Selector.Multi
                  allowExclusion={true}
                  binder={roleBinder}
                  placeholder="Abordagem"
                  title="Abordagem"
                  options={
                    roles?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingRoles}
                  $fit={true}>
                  <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Selector.Multi
                  allowExclusion={true}
                  binder={keyMessageBinder}
                  placeholder="Mensagem chave"
                  title="Mensagem chave"
                  options={
                    keyMessages?.map((item) => ({
                      text: Str.from(item.message).initCap,
                      value: item.internalID,
                    })) ?? []
                  }
                  loading={isSearchingKeyMessages}
                  $fit={true}>
                  <Selector.Icon value={Star} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Selector.Multi
                  allowExclusion={true}
                  allowClear={true}
                  binder={themeGroupBinder}
                  placeholder="Grupo"
                  title="Grupo"
                  options={
                    themeGroups?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingThemeGroups}
                  $fit={true}>
                  <Selector.Icon value={BookmarkOne} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Selector.Multi
                  allowExclusion={true}
                  binder={themeBinder}
                  placeholder={themeGroupBinder.selectedOptions.length === 0 ? 'Selecione um grupo' : 'Tema'}
                  title="Tema"
                  options={groupThemesByName(themes ?? []).map((item) => ({
                    text: Str.from(item.text).initCap,
                    value: item.value,
                  }))}
                  loading={isSearchingThemes || isRestoringThemeFilter}
                  disabled={themeGroupBinder.selectedOptions.length === 0}
                  $fit={true}>
                  <Selector.Icon value={Tag} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
              </FilterGroupLine>
              <FilterGroupLine className="tertiary-filters">
                <Selector.Multi
                  allowExclusion={true}
                  binder={approachBinder}
                  placeholder="Formato"
                  title="Formato"
                  options={
                    approaches?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingApproaches}
                  $fit={true}>
                  <Selector.Icon value={Target} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Selector.Multi
                  allowExclusion={true}
                  binder={publisherBinder}
                  placeholder="Publicador"
                  title="Publicador"
                  options={
                    publishers?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingPublishers}
                  $fit={true}>
                  <Selector.Icon value={People} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>

                <Selector.Multi
                  allowExclusion={true}
                  binder={sourceBinder}
                  placeholder="Fonte"
                  title="Fonte"
                  options={[
                    { text: 'Redes', value: 1 },
                    { text: 'Imprensa', value: 2 },
                  ]}
                  $fit={true}>
                  <Selector.Icon value={SpeakerOne} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
              </FilterGroupLine>
              <FilterGroupLine className="quaternary-filters">
                <Selector.Multi
                  allowExclusion={true}
                  binder={spokespersonBinder}
                  placeholder="Porta-Voz"
                  title="Porta-Voz"
                  options={
                    spokespersons?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingSpokespersons}
                  $fit={true}>
                  <Selector.Icon value={SpeakerOne} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>

                <Selector.Multi
                  allowExclusion={true}
                  binder={profileBinder}
                  placeholder="Perfil de Rede Social"
                  title="Perfil de Rede Social"
                  options={
                    profiles?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingProfiles}
                  onSearch={handleProfileSearch}
                  $fit={true}>
                  <Selector.Icon value={Avatar} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Flex $type="row" $justify="space-between" $align="center" $gap={8} style={{ flex: 1 }}>
                  <Selector.Default
                    placeholder="Engajamento e Interações"
                    options={engagementOptions}
                    {...engagementTypeBinder}
                    $fit={true}>
                    <Selector.Icon value={TrendingUp} color={Tokens.ColorUIPrimary} />
                  </Selector.Default>
                  <Input placeholder="Min" type="number" min={0} {...engagementMinBinder} />
                  <Input placeholder="Max" type="number" min={0} {...engagementMaxBinder} />
                </Flex>
              </FilterGroupLine>
              <FilterGroupLine className="tertiary-filters">
                <Selector.Default
                  placeholder="Título igual conteúdo"
                  options={hasContentIssuesOptions}
                  {...hasContentIssuesBinder}
                  $fit={true}>
                  <Selector.Icon value={Editor} color={Tokens.ColorUIPrimary} />
                </Selector.Default>
                <Selector.Default placeholder="Revisão" options={REVIEWED_OPTIONS} {...reviewedBinder} $fit={true}>
                  <Selector.Icon value={CheckOne} color={Tokens.ColorUIPrimary} />
                </Selector.Default>
                <Selector.Multi
                  allowExclusion={true}
                  binder={socialChannelBinder}
                  placeholder="Canal"
                  title="Canal"
                  options={
                    socialChannels?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingChannels}
                  $fit={true}>
                  <Selector.Icon value={Earth} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Selector.Multi
                  allowExclusion={true}
                  binder={pressChannelBinder}
                  placeholder="Veículo"
                  title="Veículo"
                  options={
                    pressChannels?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingChannels}
                  $fit={true}>
                  <Selector.Icon value={Earth} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
              </FilterGroupLine>
              <FilterGroupLine className="quinary-filters">
                <Flex $type="row" $justify="flex-start" $align="center" $gap={8} style={{ flex: 1, minWidth: '540px' }}>
                  <Input placeholder="Conteúdo" icon={DocumentFolder} {...contentBinder} />
                  <Tooltip value={tooltipContent}>
                    <div style={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                      <Icon size={18} value={Help} color={Tokens.ColorUIWarnDark} />
                    </div>
                  </Tooltip>
                </Flex>
                <Selector.Default
                  placeholder="Edição Humana"
                  options={hasChangesOptions}
                  {...hasChangesBinder}
                  $fit={false}>
                  <Selector.Icon value={Editor} color={Tokens.ColorUIPrimary} />
                </Selector.Default>
                <Selector.Multi
                  allowExclusion={true}
                  binder={tierBinder}
                  placeholder="Tier"
                  title="Tier"
                  options={
                    tiers?.map((item) => ({
                      text: Str.from(item.text).initCap,
                      value: item.value,
                    })) ?? []
                  }
                  loading={isSearchingTiers}
                  $fit={false}>
                  <Selector.Icon value={MaslowPyramids} color={Tokens.ColorUIPrimary} />
                </Selector.Multi>
                <Button.Root
                  $variant="primary"
                  onClick={handleApplyFilters}
                  style={{ width: '180px', flexShrink: 0 }}
                  $loading={isLoadingMentions}>
                  <Button.Text value="Filtrar" />
                </Button.Root>
              </FilterGroupLine>
            </InvisibleArea>
          </MentionsFilterHeader>
        </MentionsFilterItemContainer>
      </MentionsFilterContainer>

      {showSaveModal && (
        <SaveFilterModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          currentConfig={getCurrentFilterConfig()}
          filterType="MENTION"
        />
      )}

      {showLoadModal && (
        <LoadFilterModal
          isOpen={showLoadModal}
          onClose={() => setShowLoadModal(false)}
          onLoad={applyFilterConfig}
          filterType="MENTION"
        />
      )}
    </FilterGroup>
  )
}
